import React, { useCallback, useEffect, useMemo, useState } from 'react'
import ContentHeader from '../ContentHeader'
import { useI18n } from 'contexts/AuthContext'
import { Button } from 'antd'
import congratsIconEN from '../img/ic-congrats-wlc-en.png'
import congratsIconES from '../img/ic-congrats-wlc-es.png'
import congratsIconPT from '../img/ic-congrats-wlc-pt.png'
import congratsIconFR from '../img/ic-congrats-wlc-fr.png'
import congratsIconDE from '../img/ic-congrats-wlc-de.png'
import { logAmplitudeEvent } from 'utils/amplitudeInit'
import {
  updateUserPromise,
  joinSkuProgramsPromise,
  mcaScreenerPromise,
} from 'apis/FitOnCareAPI'
import { logBrazeEvent } from 'utils/brazeInit'
import _ from 'lodash'
import { useUserProfileStatus } from 'hooks/useUserProfileStatus'
import { useApplyNewPatientTag } from 'hooks/useApplyTags'
import { onCareFlowFinished } from '../Rehab/util'
import { getLocalLanguage } from 'utils/authLocalStorage'
import { useCandidateTracks } from 'contexts/AuthContext'
import { ScreenKey } from '../util'

const SkuCongratulations = (props) => {
  const { i18n, localCode } = useI18n()
  const { sku, fitonUserId, from } = props
  const [programIds, setProgramIds] = useState([])
  const [isJoined, setJoined] = useState(false)
  const { getUserProfileStatus } = useUserProfileStatus()
  const { applyNewPatientTag } = useApplyNewPatientTag()
  const { candidateTracks } = useCandidateTracks()
  const [currentFitonUserId, setCurrentFitonUserId] = useState(fitonUserId)

  useEffect(() => {
    props.setRemoveEventListener(true)
  }, [])

  useEffect(() => {
    if (sku === 'mca') {
      mcaScreenerPromise().then((resp) => {
        const fitonUserId = resp.data?.fitonUserId?.toString()
        setCurrentFitonUserId(fitonUserId)
      })
    }
  }, [sku])

  useEffect(() => {
    setCurrentFitonUserId(fitonUserId)
  }, [fitonUserId])

  const joinSkuPrograms = useCallback(async () => {
    if (currentFitonUserId && sku) {
      setJoined(false)
      const response = await joinSkuProgramsPromise({
        userId: currentFitonUserId,
        sku: sku === 'mca' ? 'wlc' : sku,
        isMca: sku === 'mca',
      })
      setProgramIds(response?.data?.programIds || [])
    }
    setJoined(true)
  }, [currentFitonUserId, sku])

  useEffect(() => {
    joinSkuPrograms()
  }, [joinSkuPrograms])

  const hasMskTracks = useMemo(() => {
    return (
      sku === 'mca' &&
      candidateTracks?.length > 1 &&
      candidateTracks?.some((track) => track === 'msk')
    )
  }, [candidateTracks, sku])

  const logEvent = useCallback(async () => {
    const { shouldSkipFlow } = await getUserProfileStatus()
    logAmplitudeEvent('Screen View: Onboarding - Signup Success', {
      sku: sku,
      ...(shouldSkipFlow ? { flow_skip: 1 } : {}),
      language: getLocalLanguage(),
      ...(sku === 'mca' ? { tracks: ['wlc'] } : {}),
    })
  }, [getUserProfileStatus, sku])

  const applyTags = useCallback(async () => {
    const { shouldSkipFlow } = await getUserProfileStatus()
    if (!shouldSkipFlow) {
      applyNewPatientTag()
    }
  }, [])

  useEffect(() => {
    applyTags()
  }, [applyTags])

  useEffect(() => {
    if (hasMskTracks) {
      logAmplitudeEvent('Screen View: Onboarding - Signup Success', {
        sku: sku,
        language: getLocalLanguage(),
        ...(sku === 'mca' ? { tracks: ['wlc'] } : {}),
      })
      return
    }
    logEvent()
    logBrazeEvent(`User Approved - ${sku}`)
    updateUserPromise({
      skuStatus: {
        sku,
        status: 2,
      },
    })
  }, [sku, logEvent, hasMskTracks])

  const onNext = () => {
    if (sku === 'mca') {
      logAmplitudeEvent('Click: Onboarding - Signup Success - Continue', {
        sku,
        destination: 'wlc',
      })
    }
    if (hasMskTracks) {
      props.setCurrentKey(ScreenKey.REHAB_CONGRATULATIONS)
      return
    }
    const minProgramId = !_.isEmpty(programIds) ? Math.min(...programIds) : 0
    onCareFlowFinished({
      sku,
      success: true,
      programId: minProgramId,
      from,
      fitonUserId: currentFitonUserId,
    })
  }

  const onBack = (e) => {
    props.setRemoveEventListener(false)
    props.onBack(e)
  }

  const getCongratsIcon = () => {
    switch (localCode) {
      case 'pt':
        return congratsIconPT
      case 'fr':
        return congratsIconFR
      case 'de':
        return congratsIconDE
      case 'es':
        return congratsIconES
      default:
        return congratsIconEN
    }
  }

  return (
    <div className='info-sec' style={{ minHeight: '100%' }}>
      <ContentHeader {...props} onBack={onBack} />
      <div className='info-content'>
        <img
          src={getCongratsIcon()}
          alt=''
          className='top-icon'
          style={{
            height: '200px',
            width: '200px',
            margin: '50px auto 0',
            borderRadius: '16px',
            objectFit: 'cover',
            objectPosition: 'center',
          }}
        />
        <div className='title'>{i18n('You are in!')}</div>
        <div
          className='subtitle'
          style={{ marginBottom: '10px', marginTop: '17px' }}
        >
          {sku === 'mca' ? (
            <>
              {i18n(
                `{0} isn't just about weight. It's a clinical program focused on improving cardiometabolic health — blood sugar, blood pressure, cholesterol, and inflammation.`,
                <b>{i18n('The Weight Loss Companion')}</b>
              )}
            </>
          ) : (
            <>
              {i18n(
                `The Weight Loss Companion is a clinically focused, behavioral health program billed through your health insurance plan that will help you build healthy habits that last.`
              )}
            </>
          )}
        </div>
        <Button
          loading={!isJoined}
          className='btn-primary full-width-btn'
          onClick={onNext}
        >
          {sku === 'mca' ? i18n('Get Started') : i18n('Start Program')}
        </Button>
        <div className='bottom-tips'>
          {sku === 'mca' ? (
            <>
              {i18n(
                'This program is intended to support your wellness and does not replace advice from healthcare providers. If you experience a medical emergency or severe symptoms, call 911 or seek immediate medical attention.'
              )}
            </>
          ) : (
            <>
              {i18n(
                'By clicking ‘Start Program’, you acknowledge you are located in a US state in which FitOn Care is authorized to operate ({0}) and accept responsibility for any co-pay, deductible, coinsurance, or other out-of-pocket costs if the program is not fully covered by your health insurance plan.',
                <a
                  href='https://fitoncare.com/am-i-covered'
                  target='_blank'
                  rel='noreferrer'
                  className='gradient-font-1'
                >
                  {i18n('here')}
                </a>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default SkuCongratulations
